
import { XMarkIcon, PlusIcon, PencilIcon, CalendarIcon } from "@heroicons/vue/24/solid";
import { format, subDays, getISODay, parseISO } from "date-fns";
import { defineComponent, PropType } from "vue";
import DailyReportRepository from "@/repositories/DailyReportRepository";
import logger from "@/services/logger";
import {
  ReportComboValues,
  ReportSectionConfig,
  ReportListBasedSection,
  ReportListBasedEntry,
} from "@/types/DailyReport";
import CopySectionData from "@/views/daily_report/components/SectionCopyData.vue";
import SelectCombobox from "@/views/daily_report/components/SelectSearchCombobox.vue";

export default defineComponent({
  props: {
    entries: {
      type: Array as PropType<ReportListBasedEntry[]>,
      required: true,
    },
    section: {
      type: String as PropType<ReportListBasedSection>,
      required: true,
    },
    customFields: {
      type: Object as PropType<Record<string, ReportComboValues[]>>,
      required: true,
    },
    sectionConfig: {
      type: Object as PropType<ReportSectionConfig>,
      required: true,
    },
    editMode: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
    highlightSection: {
      type: String as PropType<string>,
      required: true,
    },
    approved: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
    validationError: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
  },
  emits: [
    "addHighlight",
    "activeEditMode",
    "addEntry",
    "removeEntry",
    "updateComboBox",
    "updateFieldValue",
    "addCustomField",
    "deleteCustomField",
    "updateSectionData",
  ],
  components: {
    PlusIcon,
    PencilIcon,
    CalendarIcon,
    XMarkIcon,
    SelectCombobox,
    CopySectionData,
  },
  data() {
    return {
      copyDataOpen: false,
      date: this.$route.params.date as string,
      searchDate: "",
    };
  },
  computed: {
    yesterday() {
      const dateObj = parseISO(this.date);
      if (getISODay(dateObj) === 1) {
        return format(subDays(dateObj, 2), "yyyy-MM-dd");
      }
      return format(subDays(dateObj, 1), "yyyy-MM-dd");
    },
    configList() {
      const result = Object.keys(this.sectionConfig).map((key) => ({
        ...this.sectionConfig[key],
        key,
      }));
      result.sort((a, b) => a.order - b.order);
      return result;
    },
  },
  methods: {
    hasKey(key: string) {
      return Object.prototype.hasOwnProperty.call(this.sectionConfig, key);
    },
    sortSavedOptions(options: Record<string, unknown>[], sectionName: string) {
      let defaultList = options.filter((option) => option.mode === "default");
      const customList = options.filter((option) => option.mode === "custom");

      defaultList = defaultList.sort((a, b) =>
        this.$t(`report.sections.${this.section}.${sectionName}_entries.${a.value}`).localeCompare(
          this.$t(`report.sections.${this.section}.${sectionName}_entries.${b.value}`),
        ),
      );
      customList.sort((a, b) => (a.value as string).localeCompare(b.value as string));

      return defaultList.concat(customList);
    },
    isFirstCol(columnName: string) {
      if (this.configList.length > 0) {
        return this.configList[0].key === columnName;
      }
    },
    focusInputField(sectionTitle: string, row: number, columName: string) {
      let element = document.getElementById(`input-${sectionTitle}-${row}-${columName}`);

      if (element !== null) {
        try {
          if (element.nodeName === "DIV") {
            const inputFields = element.querySelectorAll("input");
            if (inputFields.length > 0) {
              element = inputFields[0];
            } else {
              return;
            }
          }
          element.focus();
        } catch (error) {
          logger.error(error);
        }
      }
    },
    nextFocus(event: KeyboardEvent, rowIdx: number, columName: string) {
      if (!event.shiftKey && !event.altKey && !event.ctrlKey) {
        if (this.configList.length > 0) {
          const configListCurrentIndex = this.configList.findIndex(
            (item) => item.order === this.sectionConfig[columName].order,
          );

          if (configListCurrentIndex + 1 < this.configList.length) {
            this.focusInputField(
              this.section,
              rowIdx,
              this.configList[configListCurrentIndex + 1].key as string,
            );
          } else {
            if (rowIdx + 1 < this.entries.length) {
              this.focusInputField(this.section, rowIdx + 1, this.configList[0].key as string);
            } else {
              this.addNewEntry();
            }
          }
        }
      }
    },
    activeEditMode() {
      this.$emit("activeEditMode");
      this.$emit("addHighlight");
    },

    addNewEntry() {
      this.$emit("activeEditMode");

      const newEntry = {} as Record<string, unknown>;

      for (const [key, v] of Object.entries(this.sectionConfig)) {
        const value = v as Record<string, unknown>;

        if (value["field_type"] === "combobox") {
          newEntry[key] = {
            mode: "custom",
            value: "",
          };
        }
        if (value["field_type"] === "number") {
          newEntry[key] = 0;
        }
        if (value["field_type"] === "text") {
          newEntry[key] = "";
        }
        if (value["field_type"] === "textarea") {
          newEntry[key] = "";
        }
      }
      this.$emit("addEntry", newEntry, this.section);
    },
    copySectionData(date: string) {
      const { customer_name, site_id } = this.$route.params;

      return DailyReportRepository.getSectionValueCopy(
        this.section,
        customer_name as string,
        site_id as string,
        date,
      )
        .then((response) => {
          if (response.data.length === 0) {
            this.showToast("warning", this.$t("err.saved_values"));
          } else {
            this.$emit("updateSectionData", response.data);
          }
        })
        .catch(() => {
          this.showToast("warning", this.$t("err.saved_values"));
        })
        .finally(() => this.$emit("activeEditMode"));
    },
  },
});
